<template>
  <div class="content statement-template-list">
    <div class="table-operator" style="margin-bottom: 16px">
      <a-button type="primary" icon="plus" @click="handleAdd">
        {{ $t('创建财务报表模板') }}
      </a-button>
    </div>
    <div class="form-content">
      <a-row :gutter="16" style="margin-top: 15px">
        <a-col :span="6">
          <CommonSelect
            :isBu="true"
            :list="settlementBuList"
            :placeholder="$t('结算组织')"
            :code.sync="queryParam.business_unit_code"
            @change="handleSearch"
          />
        </a-col>
        <a-col :span="6">
          <a-input
            v-model="queryParam.name_or_code"
            :placeholder="$t('请输入模板编码/名称')"
            allowClear
            @pressEnter="handleSearch"
          />
        </a-col>
        <a-col :span="6">
          <CommonSelect
            :list="reportTypeList"
            :placeholder="$t('报表类型')"
            :code.sync="queryParam.type"
            @change="handleSearch"
          />
        </a-col>
        <a-col :span="6">
          <div>
            <a-button type="primary" @click="handleSearch">
              <i class="iconfont" style="margin-right: 5px">&#xe647;</i>
              {{ $t('查询') }}
            </a-button>
            <a-button style="margin-left: 8px" @click="resetQuery">
              <a-icon type="redo" />
              {{ $t('重置') }}
            </a-button>
          </div>
        </a-col>
      </a-row>
    </div>

    <a-table
      :columns="columns"
      :data-source="dataList"
      :loading="loading"
      :pagination="false"
      rowKey="id"
      size="small"
      bordered
    >
      <span slot="type" slot-scope="text">
        <a-tag :color="text === 'INCOME' ? 'green' : text === 'CASH_FLOW' ? 'cyan' : 'blue'">
          {{ reportTypeMap[text] || text || '-' }}
        </a-tag>
      </span>
      <span slot="is_preset" slot-scope="text">
        <a-tag :color="text === 1 ? 'purple' : 'gray'">
          {{ text === 1 ? $t('系统预置') : $t('自定义') }}
        </a-tag>
      </span>
      <span slot="status" slot-scope="text, record">
        <a-switch
          :checked="text === 'ENABLED'"
          :loading="statusLoadingId === record.id"
          size="small"
          @change="(checked) => handleStatusChange(record, checked)"
        />
      </span>
      <span slot="action" slot-scope="text, record">
        <a @click="handleDetail(record)">{{ $t('详情') }}</a>
      </span>
    </a-table>

    <!-- 分页组件 -->
    <div style="margin-top: 16px; display: flex; justify-content: flex-end">
      <pagination
        :pageNo="pagination.current"
        :length="pagination.pageSize"
        :total-count="pagination.total"
        @paginate="paginate"
      />
    </div>

    <!-- 新增/编辑弹窗 -->
    <a-modal
      :title="modalTitle"
      :visible="modalVisible"
      :confirmLoading="confirmLoading"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
      width="800px"
    >
      <a-form-model ref="ruleForm" :model="form" :rules="rules" layout="vertical">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item :label="$t('结算组织')" prop="business_unit_code">
              <CommonSelect
                :list="settlementBuList"
                :placeholder="$t('请选择结算组织')"
                :code.sync="form.business_unit_code"
                :disabled="form.id !== undefined"
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('报表类型')" prop="type">
              <CommonSelect
                :list="reportTypeList"
                :placeholder="$t('请选择报表类型')"
                :code.sync="form.type"
                :disabled="form.id !== undefined"
              />
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item :label="$t('模板编码')" prop="code">
              <a-input
                v-model="form.code"
                :placeholder="$t('请输入模板编码，未传使用系统默认编码')"
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('模板名称')" prop="name">
              <a-input
                v-model="form.name"
                :placeholder="$t('请输入模板名称，未传使用系统默认名称')"
              />
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="24">
            <a-form-model-item :label="$t('报表项目')" prop="statement_item_code_list">
              <a-select
                v-model="form.statement_item_code_list"
                mode="multiple"
                style="width: 100%"
                :placeholder="$t('请选择报表项目，为空时按报表类型选择全部启用项目')"
                option-filter-prop="children"
                allowClear
              >
                <a-select-option
                  v-for="item in filteredReportItems"
                  :key="item.code"
                  :value="item.code"
                >
                  {{ item.name }} ({{ item.code }}) [{{ item.item_type_name || '-' }}]
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
        </a-row>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import http from '@/service/axios'
import api from '@/service/api'
import pagination from '@/component/pagination.vue'

export default {
  name: 'StatementTemplateList',
  components: { pagination },
  data() {
    return {
      loading: false,
      confirmLoading: false,
      statusLoadingId: undefined,
      modalVisible: false,
      modalTitle: this.$t('创建财务报表模板'),
      queryParam: {
        business_unit_code: undefined,
        name_or_code: undefined,
        type: undefined,
      },
      dataList: [],
      allReportItems: [],
      reportTypeList: [
        { code: 'INCOME', name: this.$t('利润表') },
        { code: 'CASH_FLOW', name: this.$t('现金流量表') },
        { code: 'BALANCE_SHEET', name: this.$t('资产负债表') },
      ],
      reportTypeMap: {
        INCOME: this.$t('利润表'),
        CASH_FLOW: this.$t('现金流量表'),
        BALANCE_SHEET: this.$t('资产负债表'),
      },
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
      },
      columns: [
        { title: this.$t('模板编码'), dataIndex: 'code', width: 180 },
        { title: this.$t('模板名称'), dataIndex: 'name', width: 220 },
        { title: this.$t('结算组织'), dataIndex: 'business_unit_name', width: 200 },
        {
          title: this.$t('报表类型'),
          dataIndex: 'type',
          scopedSlots: { customRender: 'type' },
          width: 140,
        },
        {
          title: this.$t('系统预置'),
          dataIndex: 'is_preset',
          scopedSlots: { customRender: 'is_preset' },
          width: 100,
          align: 'center',
        },
        { title: this.$t('创建人'), dataIndex: 'created_user', width: 120 },
        { title: this.$t('创建时间'), dataIndex: 'created_at', width: 180 },
        {
          title: this.$t('状态'),
          dataIndex: 'status',
          scopedSlots: { customRender: 'status' },
          width: 100,
          align: 'center',
        },
        {
          title: this.$t('操作'),
          key: 'action',
          width: 100,
          scopedSlots: { customRender: 'action' },
        },
      ],
      form: {
        id: undefined,
        business_unit_code: undefined,
        code: '',
        name: '',
        type: undefined,
        statement_item_code_list: [],
      },
      rules: {
        business_unit_code: [
          { required: true, message: this.$t('请选择结算组织'), trigger: 'change' },
        ],
        type: [{ required: true, message: this.$t('请选择报表类型'), trigger: 'change' }],
      },
    }
  },
  computed: {
    ...mapState(['settlementBuList']),
    filteredReportItems() {
      // If template type is chosen, we could optionally filter report items matching that type.
      // But statement item list endpoint from the server will return all items.
      // We can display all or filter them if the codes correlate.
      // Let's show all report items so user has maximum flexibility, but filter by status = 1 (enabled).
      return this.allReportItems.filter((item) => item.status === 1)
    },
  },
  created() {
    this.loadData()
    this.loadReportItems()
  },
  methods: {
    async loadReportItems() {
      try {
        await http({
          url: api.getFinancialStatementItemList,
          type: 'post',
          data: {
            page_no: 1,
            page_size: 1000,
          },
          success: (res) => {
            if (res.success && res.result) {
              const data = res.result
              this.allReportItems = data.list || []
            }
          },
        })
      } catch (error) {
        console.error('Failed to load statement items', error)
      }
    },
    async loadData() {
      if (!this.queryParam.business_unit_code) return
      this.loading = true
      try {
        await http({
          url: api.getFinancialStatementTemplateList,
          type: 'post',
          data: {
            business_unit_code: this.queryParam.business_unit_code,
            name_or_code: this.queryParam.name_or_code,
            type: this.queryParam.type,
            page_no: this.pagination.current,
            page_size: this.pagination.pageSize,
          },
          success: (res) => {
            if (res.success && res.result) {
              const data = res.result
              this.dataList = data.list || []
              this.pagination.total = data.total || 0
            }
          },
        })
      } catch (error) {
        console.error('Failed to load statement templates', error)
      } finally {
        this.loading = false
      }
    },
    paginate(data) {
      this.pagination.current = data.pageNo
      this.pagination.pageSize = data.pageSize
      this.loadData()
    },
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    resetQuery() {
      this.queryParam = {
        business_unit_code: undefined,
        name_or_code: undefined,
        type: undefined,
      }
      this.handleSearch()
    },
    handleTableChange(pagination) {
      if (pagination && pagination.current) {
        this.pagination.current = pagination.current
        this.pagination.pageSize = pagination.pageSize
        this.loadData()
      }
    },
    handleAdd() {
      this.modalTitle = this.$t('创建财务报表模板')
      this.form = {
        id: undefined,
        business_unit_code: undefined,
        code: '',
        name: '',
        type: undefined,
        statement_item_code_list: [],
      }
      this.modalVisible = true
      this.$nextTick(() => {
        if (this.$refs.ruleForm) {
          this.$refs.ruleForm.clearValidate()
        }
      })
    },
    async handleStatusChange(record, checked) {
      const status = checked ? 'ENABLED' : 'DISABLED'
      const oldStatus = record.status
      record.status = status
      this.statusLoadingId = record.id
      try {
        await http({
          url: api.updateFinancialStatementTemplateStatus,
          type: 'post',
          data: {
            id: record.id,
            status,
          },
          success: (res) => {
            if (res.success) {
              this.$message.success(this.$t('操作成功'))
            } else {
              record.status = oldStatus
              this.$message.error(res.msg || this.$t('操作失败'))
            }
          },
        })
      } catch (error) {
        record.status = oldStatus
      } finally {
        this.statusLoadingId = undefined
      }
    },
    handleDetail(record) {
      this.$router.push({
        name: 'statementTemplateDetail',
        query: { id: record.id },
      })
    },
    handleModalOk() {
      this.$refs.ruleForm.validate(async (valid) => {
        if (!valid) return
        this.confirmLoading = true
        try {
          await http({
            url: api.createOrUpdateFinancialStatementTemplate,
            type: 'post',
            data: {
              ...this.form,
            },
            success: (res) => {
              if (res.success) {
                this.$message.success(this.form.id ? this.$t('编辑成功') : this.$t('新增成功'))
                this.modalVisible = false
                this.loadData()
              } else {
                this.$message.error(res.msg || this.$t('操作失败'))
              }
            },
          })
        } catch (error) {
          this.$message.error(this.$t('操作发生错误'))
        } finally {
          this.confirmLoading = false
        }
      })
    },
    handleModalCancel() {
      this.modalVisible = false
    },
  },
}
</script>
